import { ChevronRight } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"

type Props = {
  onConfirm: () => void
  label?: string
  className?: string
  isDanger?: boolean
}

const THUMB_SIZE = 40
const CONFIRM_THRESHOLD = 0.88

export const SlideToConfirm = ({
  onConfirm,
  label = "Slide to confirm",
  className,
  isDanger = false,
}: Props) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [trackWidth, setTrackWidth] = useState(0)
  const [offset, setOffset] = useState(0)
  const [confirmed, setConfirmed] = useState(false)
  const dragStartX = useRef(0)
  const isDragging = useRef(false)

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const update = () => setTrackWidth(Math.max(0, el.clientWidth - THUMB_SIZE))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (confirmed) return
    isDragging.current = true
    dragStartX.current = e.clientX - offset
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return
    const next = Math.min(
      Math.max(0, e.clientX - dragStartX.current),
      trackWidth,
    )
    setOffset(next)
    if (trackWidth > 0 && next >= trackWidth * CONFIRM_THRESHOLD) {
      isDragging.current = false
      try {
        e.currentTarget.releasePointerCapture(e.pointerId)
      } catch {
        // pointer may already be released
      }
      setOffset(trackWidth)
      setConfirmed(true)
      onConfirm()
    }
  }

  const handlePointerUp = () => {
    if (!isDragging.current) return
    isDragging.current = false
    setOffset(0)
  }

  const progress = offset / (trackWidth || 1)

  return (
    <div
      ref={trackRef}
      className={cn(
        "relative h-10 select-none overflow-hidden rounded-full border-2 border-[#64748B] bg-muted",
        className,
      )}
    >
      <div
        className={cn(
          "absolute inset-y-0 left-0 rounded-full transition-none",
          isDanger ? "bg-red-500/25" : "bg-[#2f8f36]/25",
        )}
        style={{ width: offset + THUMB_SIZE }}
      />
      <span
        className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-medium text-muted-foreground"
        style={{ opacity: Math.max(0, 1 - progress * 2) }}
      >
        {label}
      </span>
      <div
        className={cn(
          "absolute top-0 flex size-10 cursor-grab items-center justify-center rounded-full text-white shadow-md active:cursor-grabbing",
          isDanger ? "bg-red-600" : "bg-[#2f8f36]",
        )}
        style={{ left: offset, touchAction: "none" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <ChevronRight className="size-5" />
      </div>
    </div>
  )
}
